import { clsx } from 'clsx';
import React, { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Tooltip } from '@/app/components/Tooltip';
import { TooltipWrapper } from '@/app/components/Tooltip/components/TooltipWrapper';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { Input } from '@/app/components/form/Input';
import { PhoneInput } from '@/app/components/form/PhoneInput';
import type { CreateClientFormData, RelatedPassengerBasePath } from '@/types/clientsApiTypes';
import {
  additionalEmailAtPath,
  additionalEmailsPath,
  primaryEmailPath,
  type ClientEmailsFieldPrefix,
} from '../ClientEmails/types-util';
import {
  phoneItemPath,
  phonesArrayPath,
  primaryPhonePath,
  type ClientPhonesFieldPrefix,
} from '../ClientPhones/types-util';
import DangerIcon from '/public/icons/delete.svg';
import LockIcon from '/public/icons/lock.svg';
import PlusIcon from '/public/icons/plus.svg';

const MAX_EMAILS_COUNT = 2;

type RelatedPassengerClientFieldPrefix = `${RelatedPassengerBasePath}.client`;

interface RelatedPassengerAdditionalPhonesAndEmailsProps<T extends CreateClientFormData = CreateClientFormData> {
  showChildTooltip?: boolean;
  deleteButtonsType?: 'small' | 'large';
  isChild: boolean;
  fieldPrefix: (ClientEmailsFieldPrefix<T> & ClientPhonesFieldPrefix<T>) | RelatedPassengerClientFieldPrefix;
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const RelatedPassengerAdditionalPhonesAndEmails = <T extends CreateClientFormData = CreateClientFormData>({
  showChildTooltip,
  isChild,
  fieldPrefix,
  deleteButtonsType = 'small',
  onUpdateFormData,
}: RelatedPassengerAdditionalPhonesAndEmailsProps<T>) => {
  const {
    watch,
    formState: { errors },
    register,
    setError,
    clearErrors,
    setValue,
  } = useFormContext<T>();

  const clientFieldPrefix = fieldPrefix as ClientEmailsFieldPrefix<T> & ClientPhonesFieldPrefix<T>;

  const primaryEmail = watch(primaryEmailPath(clientFieldPrefix));
  const primaryPhone = watch(primaryPhonePath(clientFieldPrefix));

  const hasPrimaryEmail = Boolean(String(primaryEmail ?? '').trim());
  const hasPrimaryPhone = Boolean(String(primaryPhone ?? '').trim());

  const formErrors = errors as { primary_passenger?: { client?: { email?: unknown; phone?: unknown } } };
  const isPrimaryEmailValid =
    !!primaryEmail &&
    !(String(fieldPrefix) === 'primary_passenger.client' ? formErrors?.primary_passenger?.client?.email : undefined);

  const primaryPhoneError = formErrors?.primary_passenger?.client?.phone;
  const isPrimaryPhoneValid = useMemo(() => {
    const phone = String(primaryPhone ?? '').trim();
    if (!phone) return false;
    if (primaryPhoneError) return false;
    if (phone.length < 3) return false;
    return true;
  }, [primaryPhone, primaryPhoneError]);

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    name: additionalEmailsPath(clientFieldPrefix),
  });

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    name: phonesArrayPath(clientFieldPrefix),
  });

  const canAddEmail = (isChild || isPrimaryEmailValid) && emailFields.length < MAX_EMAILS_COUNT;
  const canAddPhone = (isChild || isPrimaryPhoneValid) && true;

  const onAddEmail = () => {
    if (emailFields.length < MAX_EMAILS_COUNT) {
      appendEmail({ email: '' } as { email: string });
    }
  };

  const onDeleteEmail = (index: number) => {
    removeEmail(index);
    onUpdateFormData?.(
      `${fieldPrefix}.additional_emails`,
      emailFields.filter((_, i) => i !== index)
    );
  };

  const onDeletePhone = (index: number) => {
    removePhone(index);
    onUpdateFormData?.(
      phonesArrayPath(clientFieldPrefix),
      phoneFields.filter((_, i) => i !== index)
    );
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Tooltip
          placement="top-start"
          forcedOpen={showChildTooltip && hasPrimaryEmail}
          isVisible={isChild}
          content={<TooltipWrapper>Same as primary passenger</TooltipWrapper>}
        >
          <div className="w-full">
            <Input
              label="Primary email"
              required
              placeholder="example@smartflyer.com"
              disabled={isChild}
              intent="primaryWhite"
              className={clsx('w-full', isChild && 'cursor-not-allowed')}
              {...register(primaryEmailPath(clientFieldPrefix))}
              errors={errors}
              onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
            />
          </div>
        </Tooltip>
        <Tooltip
          placement="top-start"
          forcedOpen={showChildTooltip && hasPrimaryPhone}
          isVisible={isChild}
          content={<TooltipWrapper>Same as primary passenger</TooltipWrapper>}
        >
          <div className="w-full ml-1">
            <PhoneInput
              name={primaryPhonePath(clientFieldPrefix) as never}
              value={String(primaryPhone ?? '')}
              disableDialCodePrefill
              onChange={(v) =>
                setValue(primaryPhonePath(clientFieldPrefix), (v?.phoneWithoutMask ?? '') as never, {
                  shouldValidate: true,
                })
              }
              setError={setError}
              clearErrors={clearErrors}
              label="Primary phone number"
              placeholder="Enter phone number"
              className={clsx(isChild && 'cursor-not-allowed')}
              errors={errors}
              disabled={isChild}
              onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
            />
          </div>
        </Tooltip>

        {isChild && (
          <div className="col-span-full flex items-center justify-start gap-[6px] text-body-small text-neutral-600">
            <LockIcon className="w-[14px] h-[14px] shrink-0 fill-current text-neutral-700" aria-hidden />
            <span className="text-body-small text-neutral-700">Managed by the Primary Passenger</span>
          </div>
        )}

        <div className="flex flex-col gap-5 w-full max-md:gap-4">
          {emailFields.map((field, index) => (
            <div key={field.id} className="flex gap-2 items-start w-full">
              <div
                className={clsx(
                  'flex-1 w-full min-w-0 relative',
                  deleteButtonsType === 'small' ? 'relative' : 'flex gap-2 items-start w-full'
                )}
              >
                <Input
                  className="w-full"
                  label="Additional email"
                  placeholder="Enter email"
                  intent="primaryWhite"
                  {...register(additionalEmailAtPath(clientFieldPrefix, index))}
                  errors={errors}
                  onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
                />
                {deleteButtonsType === 'small' ? (
                  <button onClick={() => onDeleteEmail(index)} className="absolute top-0 right-0 cursor-pointer block">
                    <DangerIcon className="w-4 h-4 text-red-500" />
                  </button>
                ) : (
                  <div className="mt-7 shrink-0">
                    <IconButton
                      color="danger"
                      size="xl"
                      onClick={() => onDeleteEmail(index)}
                      icon={<DangerIcon className="w-6 h-6 text-red-500" />}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
          <BaseButton
            intent="inline"
            startIcon={<PlusIcon className="w-4 h-4" />}
            disabled={!canAddEmail}
            type="button"
            onClick={onAddEmail}
            className="text-[16px]"
          >
            Add Another Email
          </BaseButton>
        </div>

        <div className="flex flex-col gap-5 ml-1 max-md:gap-4">
          {phoneFields.map((field, index) => (
            <div
              key={field.id}
              className={clsx(
                'flex items-start relative',
                deleteButtonsType === 'small' ? 'relative' : 'flex gap-2 items-start w-full'
              )}
            >
              <PhoneInput
                name={phoneItemPath(clientFieldPrefix, index) as never}
                value={String(watch(phoneItemPath(clientFieldPrefix, index)) ?? '')}
                label="Additional phone number"
                placeholder="Enter phone number"
                onChange={(v) => {
                  setValue(phoneItemPath(clientFieldPrefix, index), (v?.phoneWithMask ?? '') as never, {
                    shouldValidate: true,
                  });
                }}
                errors={errors}
                setError={setError}
                className="w-full"
                clearErrors={clearErrors}
                onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
                valueType="phoneWithMask"
              />
              {deleteButtonsType === 'small' ? (
                <button onClick={() => onDeletePhone(index)} className="absolute top-0 right-0 cursor-pointer">
                  <DangerIcon className="w-4 h-4 text-red-500" />
                </button>
              ) : (
                <div className="mt-7 shrink-0">
                  <IconButton
                    color="danger"
                    size="xl"
                    onClick={() => onDeletePhone(index)}
                    icon={<DangerIcon className="w-6 h-6 text-red-500" />}
                  />
                </div>
              )}
            </div>
          ))}
          <BaseButton
            intent="inline"
            startIcon={<PlusIcon className="w-4 h-4" />}
            type="button"
            disabled={!canAddPhone}
            onClick={() => appendPhone({ phone: '', title: null })}
            className="text-[16px]"
          >
            Add Another Number
          </BaseButton>
        </div>
      </div>
    </div>
  );
};
